import {
  DeleteOutlined,
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import {
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import client from '@/api/client'
import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { DetachPublicIpButton } from '@/components/common/DetachPublicIpButton'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MONO_STYLE } from '@/components/common/MonoId'
import { RecordFallback } from '@/components/common/RecordFallback'
import { ResourceBillingTab } from '@/components/common/ResourceBillingTab'
import { StatusTag } from '@/components/common/StatusTag'
import { TerminateConfirmModal } from '@/components/common/TerminateConfirmModal'
import { UserEmailLink } from '@/components/common/UserEmailLink'
import { VmNameLink } from '@/components/common/VmNameLink'
import { PUBLIC_IP_TERMINAL_STATUSES } from '@/constants/statuses'
import { useAdminRecord } from '@/hooks/useAdminRecord'
import { useDetailTermination } from '@/hooks/useDetailTermination'

type UserPublicIpDto = components['schemas']['AdminUserPublicIpDto']

export default function NetworkShowPage() {
  const { id: ipId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'info'
  const { record: ip, query: ipQuery } = useAdminRecord<UserPublicIpDto>(
    'public-ips',
    ipId,
  )

  const { terminateOpen, openTerminate, closeTerminate, handleTerminate } =
    useDetailTermination({
      resourceLabel: 'Public IP',
      terminateFn: () =>
        client.POST('/v1/admin/public_ips/terminate', {
          body: { publicIpIds: [ipId ?? ''] },
        }),
      onSuccess: () => void ipQuery.refetch(),
    })

  if (!ipId) return null

  if (!ip) {
    return (
      <RecordFallback
        label="Public IP"
        isLoading={ipQuery.isLoading}
        error={ipQuery.error}
      />
    )
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/network">Network</Link> },
          { title: ip.name },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Public IP: <Typography.Text copyable>{ip.name}</Typography.Text>
        </h2>
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={openTerminate}
          disabled={PUBLIC_IP_TERMINAL_STATUSES.has(ip.status)}
        >
          Terminate
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        items={[
          {
            key: 'info',
            label: 'Info',
            icon: <InfoCircleOutlined />,
            children: (
              <IpInfoCard ip={ip} onChanged={() => void ipQuery.refetch()} />
            ),
          },
          {
            key: 'billing',
            label: 'Billing',
            icon: <DollarOutlined />,
            children: (
              <ResourceBillingTab userId={ip.userId} resourceId={ipId} />
            ),
          },
        ]}
      />

      <TerminateConfirmModal
        open={terminateOpen}
        title="Terminate Public IP"
        resourceName={ip.name}
        onConfirm={handleTerminate}
        onCancel={closeTerminate}
      />
    </div>
  )
}

function IpInfoCard({
  ip,
  onChanged,
}: {
  ip: UserPublicIpDto
  onChanged: () => void
}) {
  return (
    <Card>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="ID">
          <Typography.Text copyable style={MONO_STYLE}>
            {ip.id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Name">
          <Typography.Text copyable>{ip.name}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <StatusTag status={ip.status} />
        </Descriptions.Item>
        <Descriptions.Item label="Address">
          {ip.address ? (
            <Typography.Text copyable>{ip.address}</Typography.Text>
          ) : (
            <Tag>Pending</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag>{ip.addressType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="User">
          <UserEmailLink userId={ip.userId} />
        </Descriptions.Item>
        <Descriptions.Item label="Cluster">
          <ClusterNameLink clusterId={ip.clusterId} />
        </Descriptions.Item>
        <Descriptions.Item label="Attached To">
          {ip.attachedTo ? (
            <Space wrap>
              <VmNameLink vmId={ip.attachedTo.id} />
              {!PUBLIC_IP_TERMINAL_STATUSES.has(ip.status) && (
                <DetachPublicIpButton
                  vmId={ip.attachedTo.id}
                  ipLabel={ip.address ?? ip.name}
                  vmLabel={ip.attachedTo.name}
                  onDetached={onChanged}
                />
              )}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Created">
          <FormattedDate date={ip.createdAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Ready Since">
          {ip.readySince ? (
            <FormattedDate date={ip.readySince} fmt="PPp" />
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Last Paid">
          <FormattedDate date={ip.lastPaidAt} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Paid Until">
          <FormattedDate date={ip.paidUntil} fmt="PPp" />
        </Descriptions.Item>
        <Descriptions.Item label="Removed">
          <FormattedDate date={ip.removedAt} fmt="PPp" />
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}
